import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  ListItem,
  ListItemIcon,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { InfoOutlined, WarningOutlined } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SortableTable from "../../Components/SortableTable";
import Title from "../../Components/Title";
import {
  addWarrantyItem,
  deleteWarrantyItem,
  editWarrantyItem,
} from "../../Utils/ApiCalls";
import { getLastParameter, getLatestLifeCycleState } from "../../Utils/Common";

export default function WarrantyInformation({ deviceData, setDeviceData }) {
  const { t } = useTranslation();
  const device_id = getLastParameter();
  const [warrantyItems, setWarrantyItems] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    activeFrom: "",
    duration: "",
    warrantyVoid: false,
  });
  const [editingItem, setEditingItem] = useState(null);
  const [editingData, setEditingData] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);

  const updateWarrantyItems = (updater) => {
    setWarrantyItems((prevItems) => {
      const nextItems =
        typeof updater === "function" ? updater(prevItems) : updater;
      if (typeof setDeviceData === "function") {
        setDeviceData((prevDevice) => {
          if (!prevDevice) {
            return prevDevice;
          }
          return {
            ...prevDevice,
            WarrantyItems: nextItems,
          };
        });
      }
      return nextItems;
    });
  };

  useEffect(() => {
    if (deviceData) {
      setWarrantyItems(
        Array.isArray(deviceData.WarrantyItems) ? deviceData.WarrantyItems : [],
      );
    }
  }, [deviceData]);

  const handleSubmit = () => {
    const name = formData.name.trim();
    if (!name) {
      return;
    }
    addWarrantyItem(
      {
        deviceid: device_id,
        warrantyItem: {
          name,
          warrantyVoid: formData.warrantyVoid,
          activeFrom: formData.activeFrom || null,
          duration: formData.duration ? Number(formData.duration) : null,
        },
      },
      (result) => {
        if (!result.error) {
          setFormData({
            name: "",
            activeFrom: "",
            duration: "",
            warrantyVoid: false,
          });
          if (result.warrantyItem) {
            updateWarrantyItems((prevItems) => [
              ...prevItems,
              result.warrantyItem,
            ]);
          }
        }
      },
      undefined,
    );
  };
  const openEditDialog = (item) => {
    if (!item?.id || item.id === "default") {
      return;
    }
    setEditingItem(item);
    setEditingData({
      name: item.name || "",
      activeFrom: item.activeFrom || "",
      duration:
        item.duration === null || item.duration === undefined
          ? ""
          : String(item.duration),
      warrantyVoid: !!item.warrantyVoid,
    });
  };
  const closeEditDialog = () => {
    setEditingItem(null);
    setEditingData(null);
  };
  const handleEditSubmit = () => {
    if (!editingItem || !editingData) {
      return;
    }
    const name = editingData.name.trim();
    if (!name) {
      return;
    }
    editWarrantyItem(
      {
        warrantyItemId: editingItem.id,
        warrantyItem: {
          name,
          warrantyVoid: editingData.warrantyVoid,
          activeFrom: editingData.activeFrom || null,
          duration: editingData.duration ? Number(editingData.duration) : null,
        },
      },
      (result) => {
        if (!result.error) {
          closeEditDialog();
          if (result.warrantyItem) {
            updateWarrantyItems((prevItems) =>
              prevItems.map((item) =>
                item.id === result.warrantyItem.id ? result.warrantyItem : item,
              ),
            );
          }
        }
      },
      undefined,
    );
  };
  const confirmDelete = () => {
    if (!itemToDelete?.id || itemToDelete.id === "default") {
      setItemToDelete(null);
      return;
    }
    deleteWarrantyItem(
      { warrantyItemId: itemToDelete.id },
      (result) => {
        if (!result.error) {
          setItemToDelete(null);
          if (editingItem?.id === itemToDelete.id) {
            closeEditDialog();
          }
          updateWarrantyItems((prevItems) =>
            prevItems.filter((item) => item.id !== itemToDelete.id),
          );
        }
      },
      undefined,
    );
  };

  const getExpiryDate = (activeFrom, durationYears) => {
    if (!activeFrom || !durationYears) {
      return "-";
    }
    const base = new Date(activeFrom);
    if (Number.isNaN(base.getTime())) {
      return "-";
    }
    const expiry = new Date(base);
    expiry.setFullYear(expiry.getFullYear() + Number(durationYears));
    return expiry.toISOString().slice(0, 10);
  };

  const latestLcs = getLatestLifeCycleState(deviceData?.LifeCycleStates);
  const shouldShowDefaultItem =
    !warrantyItems?.length && latestLcs?.state === "dispatched";
  const defaultItem = shouldShowDefaultItem
    ? {
        id: "default",
        name: t("warranty.defaultItem"),
        activeFrom: latestLcs?.createdAt
          ? new Date(latestLcs.createdAt).toISOString().slice(0, 10)
          : "-",
        duration: 3,
        warrantyVoid: 0,
      }
    : null;
  const displayedItems = warrantyItems?.length
    ? warrantyItems
    : defaultItem
      ? [defaultItem]
      : [];
  const tableItems = displayedItems.map((item) => ({
    ...item,
    expiryDate: getExpiryDate(item.activeFrom, item.duration),
  }));
  const todayMs = new Date().setHours(0, 0, 0, 0);
  const parseLocalDate = (value) => {
    if (!value) {
      return null;
    }
    const parts = value.split("-");
    if (parts.length !== 3) {
      return null;
    }
    const year = Number(parts[0]);
    const month = Number(parts[1]) - 1;
    const day = Number(parts[2]);
    const parsed = new Date(year, month, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };
  const getRowStyle = (row) => {
    const style = { cursor: "pointer" };
    if (row && row.warrantyVoid) {
      return { ...style, backgroundColor: "lightcoral" };
    }
    if (!row?.expiryDate || row.expiryDate === "-") {
      return style;
    }
    const expiry = parseLocalDate(row.expiryDate);
    const expiryMs = expiry ? expiry.getTime() : null;
    if (expiryMs !== null && expiryMs <= todayMs) {
      return { ...style, backgroundColor: "lightcoral" };
    }
    return style;
  };
  const handleRowClick = (row) => {
    openEditDialog(row);
  };

  return (
    <Grid container spacing={3}>
      <Dialog
        open={!!editingItem}
        onClose={closeEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("edit")} {t("warranty.title")}
        </DialogTitle>
        <DialogContent>
          {editingData ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("warranty.name")}
                  variant="outlined"
                  value={editingData.name}
                  onChange={(e) => {
                    setEditingData({
                      ...editingData,
                      name: e.target.value,
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("warranty.activeFrom")}
                  type="date"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  value={editingData.activeFrom}
                  onChange={(e) => {
                    setEditingData({
                      ...editingData,
                      activeFrom: e.target.value,
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("warranty.duration")}
                  type="number"
                  variant="outlined"
                  value={editingData.duration}
                  inputProps={{ min: 0, max: 10 }}
                  onChange={(e) => {
                    setEditingData({
                      ...editingData,
                      duration: e.target.value,
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={editingData.warrantyVoid}
                      onChange={(e) => {
                        setEditingData({
                          ...editingData,
                          warrantyVoid: e.target.checked,
                        });
                      }}
                    />
                  }
                  label={t("warranty.warrantyVoid")}
                />
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} variant="outlined">
            {t("cancel")}
          </Button>
          <Button
            onClick={() => {
              setItemToDelete(editingItem);
            }}
            variant="contained"
            color="secondary"
          >
            {t("delete")}
          </Button>
          <Button
            onClick={handleEditSubmit}
            color="primary"
            variant="contained"
            autoFocus
          >
            {t("save")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!itemToDelete}
        onClose={() => setItemToDelete(null)}
        aria-labelledby="warranty-delete-title"
      >
        <DialogTitle id="warranty-delete-title">
          {t("delete")} {itemToDelete?.name}?
        </DialogTitle>
        <DialogActions>
          <Button variant="outlined" onClick={() => setItemToDelete(null)}>
            {t("cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={confirmDelete}
            color="secondary"
            autoFocus
          >
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
      <Grid item xs={12}>
        <Title>{t("warranty.title")}</Title>
      </Grid>
      <Grid item xs={12}>
        {tableItems?.length ? (
          <SortableTable
            headCells={[
              {
                id: "id",
                numeric: false,
                disablePadding: false,
              },
              {
                id: "name",
                numeric: false,
                disablePadding: false,
                label: t("warranty.name"),
              },
              {
                id: "activeFrom",
                numeric: false,
                disablePadding: false,
                label: t("warranty.activeFrom"),
              },
              {
                id: "duration",
                numeric: false,
                disablePadding: false,
                label: t("warranty.duration"),
              },
              {
                id: "expiryDate",
                numeric: false,
                disablePadding: false,
                label: t("warranty.expiryDate"),
              },
              {
                id: "warrantyVoid",
                numeric: false,
                disablePadding: false,
                label: t("warranty.warrantyVoid"),
              },
            ]}
            disablePagination={true}
            defaultSortRow={"activeFrom"}
            tableData={tableItems}
            rowsPerPage={25}
            getRowStyle={getRowStyle}
            handleRowClick={handleRowClick}
            specialDataFormatting={[
              {
                id: "id",
                format: (data) => {
                  if (data && data === "default") {
                    return (
                      <ListItem style={{ fontSize: 20 }}>
                        <Tooltip
                          title={
                            <Typography style={{ fontSize: 15 }}>
                              {t("warranty.defaultItemTooltip")}
                            </Typography>
                          }
                          placement="top"
                          style={{ fontSize: 20 }}
                        >
                          <ListItemIcon size="small">
                            <InfoOutlined
                              style={{ verticalAlign: "middle" }}
                              fontSize="small"
                            />
                          </ListItemIcon>
                        </Tooltip>
                      </ListItem>
                    );
                  }
                  return "";
                },
              },
              {
                id: "warrantyVoid",
                format: (data) => {
                  return data ? t("yes") : t("no");
                },
              },
              {
                id: "expiryDate",
                format: (data) => {
                  if (!data || data === "-") {
                    return data;
                  }
                  const expiry = parseLocalDate(data);
                  const expiryMs = expiry ? expiry.getTime() : null;
                  if (expiryMs !== null && expiryMs <= todayMs) {
                    return (
                      <ListItem>
                        {data}
                        <Tooltip
                          title={
                            <Typography style={{ fontSize: 15 }}>
                              {t("warranty.expired")}
                            </Typography>
                          }
                          placement="top"
                        >
                          <ListItemIcon size="small">
                            <WarningOutlined
                              style={{ verticalAlign: "middle" }}
                              fontSize="small"
                            />
                          </ListItemIcon>
                        </Tooltip>
                      </ListItem>
                    );
                  }
                  return data;
                },
              },
            ]}
          />
        ) : (
          <Typography>{t("warranty.noItems")}</Typography>
        )}
      </Grid>
      <Grid item xs={12}>
        <Title>{t("warranty.addItem")}</Title>
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label={t("warranty.name")}
          variant="outlined"
          value={formData.name}
          onChange={(e) => {
            setFormData({
              ...formData,
              name: e.target.value,
            });
          }}
          helperText={
            "Standard Warranty | Leasing Warranty | Warranty Extension"
          }
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label={t("warranty.activeFrom")}
          type="date"
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          value={formData.activeFrom}
          onChange={(e) => {
            setFormData({
              ...formData,
              activeFrom: e.target.value,
            });
          }}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label={t("warranty.duration")}
          type="number"
          variant="outlined"
          value={formData.duration}
          inputProps={{ min: 0, max: 10 }}
          onChange={(e) => {
            setFormData({
              ...formData,
              duration: e.target.value,
            });
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Button variant="outlined" onClick={handleSubmit}>
          {t("add")}
        </Button>
      </Grid>
    </Grid>
  );
}
